import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { LiveReportList } from '@futrli-sdk/predict';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportView } from '@futrli/views/views/reports/ModifyReportView/ModifyReportView';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface LockReportDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  report: LiveReportList;
}

export const LockReportDialogContainer = ({ isOpen, onClose, report }: LockReportDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();
  const isLocking = !report.is_locked;

  const view = useApiView(
    useMemo(() => new ModifyReportView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const { screen, handleAction: handleConfirm, handleClose } = useDialogWizardState({
    isLoading: view.state.isLocking,
    error: view.state.errorLocking,
    onAction: () => view.lockReport({ reportId: report.id, is_locked: isLocking }),
    onClose
  });

  const title = isLocking
    ? intl.formatMessage({ id: 'reporting.lock_report', defaultMessage: 'Lock report' })
    : intl.formatMessage({ id: 'reporting.unlock_report', defaultMessage: 'Unlock report' });

  const confirmDescription = isLocking
    ? intl.formatMessage({
      id: 'reporting.lock_report_description',
      defaultMessage: 'You will no longer be able to edit this report when locked'
    })
    : intl.formatMessage({
      id: 'reporting.unlock_report_description',
      defaultMessage: 'Unlocking this report will make it editable'
    });

  const loadingMessage = isLocking
    ? intl.formatMessage({ id: 'reporting.locking_report', defaultMessage: 'Locking report...' })
    : intl.formatMessage({ id: 'reporting.unlocking_report', defaultMessage: 'Unlocking report...' });

  const successMessage = isLocking
    ? intl.formatMessage({ id: 'reporting.report_locked', defaultMessage: 'Report locked' })
    : intl.formatMessage({ id: 'reporting.report_unlocked', defaultMessage: 'Report unlocked' });

  const errorMessage = view.state.errorLocking?.message;

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      screen={screen}
      title={title}
      subtitle={report.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={confirmDescription}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="lock-report-dialog"
    />
  );
};
